import { useEffect, useRef, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { FileText, Paperclip, X, Upload } from "lucide-react";
import { uploadFile, MAX_UPLOAD_MB } from "../lib/uploads";

/**
 * QuoteRequestDialog — opens from the CRM Reporting Log Report dialog. Lets
 * the sales associate raise a Quote Request that lands in the Quotation
 * module's "Requested" tab for Master Admin to triage and assign.
 */
export default function QuoteRequestDialog({ open, onOpenChange, client: fixedClient, onCreated }) {
  const [pickedClient, setPickedClient] = useState(null);
  const client = fixedClient || pickedClient;
  const [orderValue, setOrderValue] = useState("");
  const [files, setFiles] = useState([]);
  const [deadline, setDeadline] = useState("");
  const [commission, setCommission] = useState("");
  const [importantRemark, setImportantRemark] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setOrderValue(""); setFiles([]); setPickedClient(null); setDeadline(""); setCommission(""); setImportantRemark("");
    }
  }, [open]);

  const submit = async () => {
    if (!client?.id) { toast.error("Please select a CRM client"); return; }
    setBusy(true);
    try {
      const attachments = [];
      for (const f of files) attachments.push(await uploadFile(f, { purpose: "quote_request" }));
      await api.post("/quote-requests", {
        client_id: client.id,
        order_value: orderValue.trim(),
        attachments,
        deadline: deadline ? new Date(deadline).toISOString() : "",
        commission: commission.trim(),
        important_remark: importantRemark.trim(),
      });
      toast.success("Quote request raised");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="quote-request-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><FileText size={18} className="text-indigo-600" /> Request Quote{client ? ` — ${client.client_name}` : ""}</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">Master Admin will receive this request, assign it to a teammate, and route it through Approval before sharing back.</div>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {!fixedClient && <ClientPicker value={pickedClient} onChange={setPickedClient} />}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Order Value</Label>
            <Input value={orderValue} onChange={(e) => setOrderValue(e.target.value)} placeholder="e.g. ₹ 2,40,000" className="mt-1.5 h-9 text-sm" data-testid="quote-order-value" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">PO / Requested Item Files</Label>
            <label className="mt-1.5 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-indigo-300 bg-indigo-50/40 px-3 py-3 text-[12px] text-indigo-700 hover:bg-indigo-50" data-testid="quote-files-dropzone">
              <Upload size={14} /> Click to upload files (multiple · max {MAX_UPLOAD_MB} MB each)
              <input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
                className="hidden"
                data-testid="quote-files-input"
                onChange={(e) => {
                  const picked = Array.from(e.target.files || []);
                  const tooBig = picked.filter((f) => f.size > MAX_UPLOAD_MB * 1024 * 1024);
                  if (tooBig.length) toast.error(`${tooBig.map((f) => f.name).join(", ")} exceeds ${MAX_UPLOAD_MB} MB`);
                  setFiles((prev) => [...prev, ...picked.filter((f) => f.size <= MAX_UPLOAD_MB * 1024 * 1024)]);
                  e.target.value = "";
                }}
              />
            </label>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="quote-files-list">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1 text-[11.5px]" data-testid={`quote-file-${i}`}>
                    <span className="flex min-w-0 items-center gap-1.5"><Paperclip size={11} className="shrink-0 text-slate-400" /><span className="truncate">{f.name}</span><span className="shrink-0 text-slate-400">· {(f.size / 1024).toFixed(0)} KB</span></span>
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`quote-file-remove-${i}`}><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Deadline</Label>
              <Input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="mt-1.5 h-9 text-sm" data-testid="quote-deadline" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Commission</Label>
              <Input value={commission} onChange={(e) => setCommission(e.target.value)} placeholder="e.g. 10%" className="mt-1.5 h-9 text-sm" data-testid="quote-commission" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Important Remark</Label>
            <textarea
              value={importantRemark}
              onChange={(e) => setImportantRemark(e.target.value)}
              placeholder="Anything the team must know — special terms, contacts, urgency…"
              rows={2}
              className="mt-1.5 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700"
              data-testid="quote-important-remark"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="quote-submit">
            {busy ? (files.length ? "Uploading…" : "Raising…") : "Raise Quote Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients/search", { params: { q, limit: 20 } });
        setResults(data || []);
      } catch (e) { setResults([]); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);
  if (value) {
    return (
      <div>
        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">CRM Client *</Label>
        <div className="mt-1.5 flex items-center justify-between rounded-md border border-indigo-200 bg-indigo-50/50 px-3 py-2 text-sm" data-testid="quote-client-selected">
          <span className="font-semibold text-slate-800 truncate">{value.client_name}</span>
          <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-rose-600" data-testid="quote-client-clear"><X size={14} /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="relative">
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">CRM Client *</Label>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setShow(true); }}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 150)}
        placeholder="Search client name…"
        className="mt-1.5 h-9 text-sm"
        data-testid="quote-client-search"
      />
      {show && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg dark:bg-slate-900 dark:border-slate-700" data-testid="quote-client-results">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-slate-400">No clients found</div>
          ) : results.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(c); setQ(""); setShow(false); }}
              className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-indigo-50 dark:hover:bg-slate-800"
              data-testid={`quote-client-option-${c.id}`}
            >
              {c.client_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
